/**
 * Floating offline / sync-progress banner.
 *
 * Rewired to the offline-first write queue (#1987): pending / syncing
 * counts come from visit_sync_queue (STORES.VISIT_SYNC_QUEUE), NOT the
 * retired localStorage `rdm_offline_queue`. `isOnline` and the manual
 * "Sync Now" action come from useConnectionStatus (the same hook that
 * drives the header chip).
 *
 * Refresh strategy mirrors SyncIssuesBanner: re-read the queue on the
 * `rdm:sync-enqueued` / `rdm:sync-failed` events, plus a light interval
 * so the count drops as the engine drains rows (draining a row doesn't
 * emit an event).
 */

import { useEffect, useState } from 'react';
import { useConnectionStatus } from '../hooks/useConnectionStatus';
import { STORES, getAll } from '../utils/indexedDB';

const REFRESH_INTERVAL_MS = 3000;

export default function OfflineIndicator() {
  const { isOnline, syncNow } = useConnectionStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function refresh() {
      try {
        const rows = (await getAll(STORES.VISIT_SYNC_QUEUE)) as Array<{ status?: string }>;
        if (cancelled) return;
        // Count only DRAINABLE work — pending (queued) + syncing (in flight) —
        // so "Sync Now" always corresponds to something the engine can actually
        // push. `failed` rows are NOT counted here: they can't be cleared by
        // Sync Now and are surfaced by SyncIssuesBanner (Retry / Discard)
        // instead. Counting them here is what made the banner stick forever
        // with a dead "Sync Now" (diag #43).
        const unsynced = rows.filter(
          (r) => r.status === 'pending' || r.status === 'syncing'
        ).length;
        setPendingCount(unsynced);
        setIsSyncing(rows.some((r) => r.status === 'syncing'));
      } catch {
        if (!cancelled) {
          setPendingCount(0);
          setIsSyncing(false);
        }
      }
    }

    void refresh();
    const onEvent = () => void refresh();
    window.addEventListener('rdm:sync-enqueued', onEvent);
    window.addEventListener('rdm:sync-failed', onEvent);
    const timer = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.removeEventListener('rdm:sync-enqueued', onEvent);
      window.removeEventListener('rdm:sync-failed', onEvent);
      window.clearInterval(timer);
    };
  }, []);

  // Don't show anything if online with no pending items
  if (isOnline && pendingCount === 0) return null;

  return (
    <div
      className={`pointer-events-auto rounded-lg p-3 shadow-lg ${
        isOnline ? 'bg-amber-500' : 'bg-slate-700'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {!isOnline ? (
            <>
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-white">You're offline</p>
                {pendingCount > 0 && (
                  <p className="text-xs text-white/80">
                    {pendingCount} action{pendingCount !== 1 ? 's' : ''} waiting to sync
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              {isSyncing ? (
                <svg className="h-5 w-5 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              )}
              <div>
                <p className="text-sm font-medium text-white">
                  {isSyncing ? 'Syncing...' : 'Back online'}
                </p>
                <p className="text-xs text-white/80">
                  {pendingCount} action{pendingCount !== 1 ? 's' : ''} to sync
                </p>
              </div>
            </>
          )}
        </div>

        {isOnline && pendingCount > 0 && !isSyncing && (
          <button
            onClick={() => void syncNow()}
            className="rounded-lg bg-white/20 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-white/30"
          >
            Sync Now
          </button>
        )}
      </div>
    </div>
  );
}
